// #region angular imports
import { Component, computed, effect, inject, linkedSignal, OnDestroy, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { filter, finalize, map } from 'rxjs';
import { Select, SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { ColorPickerModule } from 'primeng/colorpicker';
import { SkeletonModule } from 'primeng/skeleton';
// #endregion

// #region app imports
import { LogoutService } from 'app/core/security/logout.service';
import { SecurityService } from 'app/core/security/security.service';
import { UserJsVm } from '../generated/Administration/Models/Users/UserJsVm';
import { QuickUserInfoService } from './quick-user-info.service';
import { NotificationService } from 'app/notification.service';
import { LinkComponent } from '../link/link.component';
import { FeatureFlagEnum } from 'app/core/enums/generated/FeatureFlagEnum';
import { ColorService } from '../services/color.service';
// #endregion

interface ActionBtn {
    label: string;
    icon: string;
    severity?: string;
    dt?: any;
    href: string;
};

@Component({
    selector: 'pcg-quick-user-info'
    , templateUrl: './quick-user-info.component.html'
    , styleUrl: './quick-user-info.component.scss'
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [ 
        FormsModule
        , ButtonModule
        , ColorPickerModule
        , DialogModule
        , PopoverModule
        , SelectModule
        , SkeletonModule
        , LinkComponent
    ]
})
export class QuickUserInfoComponent implements OnInit, OnDestroy {

    // #region injections
    sec = inject(SecurityService);
    toastr = inject(NotificationService);
    logoutService = inject(LogoutService);
    infoService = inject(QuickUserInfoService);
    router = inject(Router);
    // #endregion

    // #region View Children
    facilitySelect = viewChild<Select>('facilitySelect');
    // #endregion

    // #region User
    user = signal<UserJsVm|null>(null);
    userInitials = computed(() => `${this.user()?.firstName?.substring(0,1)}${this.user()?.lastName?.substring(0,1)}`);
    // #endregion

    // #region Facility
    facilities = computed(() => this.infoService.facilityOptions());
    selFacilityId = linkedSignal<number>(() => this.infoService.userFacilityId());
    isLoadingFacilities = computed(() => this.infoService.isLoadingFacilities());

    facilityLabel = computed(() => {
        const noMsg = "No Facility Selected"
        if (this.isOnDashboard()) {
            return this.isLoadingFacilities() 
                ? 'Org | Facility'
                : this.infoService.userFacility()?.name ?? noMsg
        } 

        return this.isLoadingFacilities() 
            ? 'Facility Identifier'
            : this.infoService.userFacility()?.identifier ?? noMsg
    });
    // #endregion

    // #region Routes
    readonly BASE_ROUTE = "admin/users/user/edit";
    readonly PROFILE_ROUTE = `${this.BASE_ROUTE}/details`;
    readonly NOTIF_ROUTE = `${this.BASE_ROUTE}/notifications`;
    readonly MOD_ROUTE = `${this.BASE_ROUTE}/module-access`;
    readonly ORG_ROUTE = `${this.BASE_ROUTE}/organizations`;
    readonly WIDGET_ROUTE = `${this.BASE_ROUTE}/widgets`;

    userProfileURL = computed(() => `${this.PROFILE_ROUTE}/${this.user()?.id}`);
    userNotificationURL = computed(() => `${this.NOTIF_ROUTE}/${this.user()?.id}`);
    userModURL = computed(() => `${this.MOD_ROUTE}/${this.user()?.id}`);
    userOrgURL = computed(() => `${this.ORG_ROUTE}/${this.user()?.id}`);
    userWidgetURL = computed(() => `${this.WIDGET_ROUTE}/${this.user()?.id}`);

    routeItems = computed<ActionBtn[]>(() => {
        const widgets: ActionBtn[] = [
            {
                label: "Profile"
                , icon: "pi-user"
                , severity: "info"
                , href: this.userProfileURL()
            }
            , {
                label: "Notification Settings"
                , icon: "pi-bell"
                , href: this.userNotificationURL()
            }
            , {
                label: "Module Access"
                , icon: "pi-user-edit"
                , severity: "warn"
                , href: this.userModURL()
            }
            , {
                label: "Organizations"
                , icon: "pi-building"
                , severity: "help"
                , href: this.userOrgURL()
            }
        ];

        if (this.user()?.featureFlags
            ?.map(o => o.featureFlagType)
            ?.includes(FeatureFlagEnum.NewDashboard)
        ) {
            widgets.push({
                label: "Widgets"
                , icon: "pi-th-large"
                , dt: this.widgetDt
                , href: this.userWidgetURL()
            });
        }

        return widgets;
    });
    // #endregion

    // #region View State
    showColor = signal<boolean>(false);
    highlightBtn = computed(() => this.infoService.highlightBtn());

    closeModal = () => {
        this.infoService.isModalOpen.set(false);
        // Only closing the Rx facility check starts its grace period; the daily confirmation is separate
        if (
            this.infoService.modalReason() === 'rx'
            && this.infoService.shouldForceOpen()
        ) { this.infoService.setLastCloseDate(); }
    }
    // #endregion

    // #region Dts
    readonly WIDGET_COLOR = "hsl(210, 60%, 30%)";
    readonly WIDGET_COLOR_HOVER = ColorService.adjustColor(this.WIDGET_COLOR, {lightness: 7});
    readonly widgetDt = {
        root: {
            primaryBackground: this.WIDGET_COLOR
            , primaryBorderColor: this.WIDGET_COLOR
            , primaryHoverBackground: this.WIDGET_COLOR_HOVER
            , primaryHoverBorderColor: this.WIDGET_COLOR_HOVER
            , primaryActiveBackground: this.WIDGET_COLOR
            , primaryActiveBorderColor: this.WIDGET_COLOR
        }
    };

    popoverDt = computed(() => ({
        root: {
            background: this.infoService.userColor()
            , borderColor: this.infoService.userDarkColor()
        }
    }));

    userBtnDt = computed(() => ({
        root: {
            primaryColor: this.infoService.userTextColor()
            , primaryBackground: this.infoService.userColor()
            , primaryBorderColor: this.infoService.userDarkColor()
            , primaryHoverColor: this.infoService.userTextColor()
            , primaryHoverBackground: this.infoService.userLightColor()
            , primaryHoverBorderColor: this.infoService.userDarkColor()
            , primaryActiveColor: this.infoService.userTextColor()
            , primaryActiveBackground: this.infoService.userDarkColor()
            , primaryActiveBorderColor: this.infoService.userDarkColor()
        }
    }));

    readonly poDts = { root: { contentPadding: 0 } };
    // #endregion
    
    // #region Life Cycle
    isOnDashboard = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd)
            , map(e => e.urlAfterRedirects === '/dashboard')
        )
        , { initialValue: this.router.url === '/dashboard' }
    );

    constructor() {
        effect(() => {
            const select = this.facilitySelect();
            const isOpened = this.infoService.isFacilitySelectOpen();
            if (
                select 
                && isOpened
            ) { select.show(); }
        });
    }

    ngOnInit() { 
        this.user.set(this.sec.getUser());
        this.infoService.user.set(this.user());
      
        if (!this.user()) { return; }
        this.getLegacySelectedFacility();
        this.getFacilities(true);
        this.infoService.getColor();
    }

    ngOnDestroy() { this.infoService.saveUserColor(); }
    // #endregion

    // #region Getters
    /**
     * @param getUserFacility On init pass in the user's current facility to make query super quick
     */
    getFacilities(getUserFacility: boolean = false) { this.infoService.fetchFacilities(getUserFacility); }
    // #endregion

    // #region Save
    saveFacility() {
        const modal = this.infoService.isModalOpen();
        const confirmedFacilityId = this.infoService.userFacilityId();
        // A failed save must not look like it worked, so put the select back on the last saved facility
        const restoreConfirmedFacility = () => this.selFacilityId.set(confirmedFacilityId);

        this.infoService.isLoadingFacilities.set(true);
        this.infoService.setUserFacility(this.user()?.id!, this.selFacilityId())
            .pipe(finalize(() => this.infoService.isLoadingFacilities.set(false)))
            .subscribe({
                next: r => {
                    if (!r.isSuccessful) {
                        this.toastr.showMessage(r.message, r.messageClass);
                        restoreConfirmedFacility();
                        return;
                    }
                    this.infoService.userFacilityId.set(this.selFacilityId());
                    this.infoService.facilityChangeNotifier.update(c => c + 1);

                    if (modal) { this.infoService.highlight(); }
                    this.infoService.resetLastCloseDate();
                }
                // The http interceptor already surfaces the error
                , error: () => restoreConfirmedFacility()
            });
    }
    // #endregion

    // #region Legacy
    /** 
     * Originally selected facility was persisted through local storage. 
     * Capture that value and save it to DB 
    */
    getLegacySelectedFacility() {
        const facility = localStorage.getItem('userSelectedFacility');
        if (!facility) { return; }
        const facilityId = JSON.parse(facility)?.id;
        this.selFacilityId.set(facilityId);
        this.saveFacility();
        localStorage.removeItem('userSelectedFacility');
    }
    // #endregion
}
